import { FC, useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import { candidaturaService } from '@/services/candidaturaService'
import {
  Candidatura,
  ConviteEmitido,
  COR_STATUS_CANDIDATURA,
  ROTULO_STATUS_CANDIDATURA,
  StatusCandidatura,
} from '@/types/candidatura'
import { formatDate } from '@/utils/formatters'
import { AnalisarCadastroDialog } from './AnalisarCadastroDialog'

/**
 * Fila de candidaturas a consultor (PDF do Consultor, Etapa 1) — visão do back-office.
 *
 * As transições oferecidas vêm do backend (`proximosStatus`), não de uma cópia da
 * máquina de estados aqui: a tela nunca oferece um movimento que o servidor recusaria.
 */
const CandidaturasPage: FC = () => {
  const { enqueueSnackbar } = useSnackbar()

  const [candidaturas, setCandidaturas] = useState<Candidatura[]>([])
  const [filtro, setFiltro] = useState<StatusCandidatura | 'TODAS'>('TODAS')
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  const [emAvaliacao, setEmAvaliacao] = useState<Candidatura | null>(null)
  const [novoStatus, setNovoStatus] = useState<StatusCandidatura | ''>('')
  const [parecer, setParecer] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [convite, setConvite] = useState<ConviteEmitido | null>(null)
  const [analisando, setAnalisando] = useState<string | null>(null)
  const [emitindo, setEmitindo] = useState(false)

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      const page = await candidaturaService.listar(filtro === 'TODAS' ? undefined : filtro)
      setCandidaturas(page.content ?? [])
    } catch (e) {
      setErro((e as { message?: string })?.message ?? 'Falha ao carregar candidaturas')
      setCandidaturas([])
    } finally {
      setCarregando(false)
    }
  }, [filtro])

  useEffect(() => {
    carregar()
  }, [carregar])

  const abrirAvaliacao = (c: Candidatura) => {
    setEmAvaliacao(c)
    setNovoStatus('')
    setParecer(c.parecer ?? '')
  }

  // O backend exige motivo ao reprovar; espelhar aqui evita a ida ao servidor.
  const parecerObrigatorio = novoStatus === 'REPROVADA'
  const podeSalvar = !!novoStatus && (!parecerObrigatorio || !!parecer.trim())

  const emitirConvite = async (c: Candidatura) => {
    setEmitindo(true)
    try {
      setConvite(await candidaturaService.emitirConvite(c.id))
      await carregar()
    } catch (e) {
      enqueueSnackbar(
        (e as { message?: string })?.message ?? 'Falha ao emitir convite',
        { variant: 'error' }
      )
    } finally {
      setEmitindo(false)
    }
  }

  /** URL absoluta montada com o host atual — o backend não conhece o domínio público. */
  const linkAbsoluto = (c: ConviteEmitido) => `${window.location.origin}${c.link}`

  const salvar = async () => {
    if (!emAvaliacao || !novoStatus || !podeSalvar) return
    setSalvando(true)
    try {
      await candidaturaService.avaliar(emAvaliacao.id, {
        novoStatus,
        parecer: parecer.trim() || undefined,
      })
      enqueueSnackbar('Avaliação registrada', { variant: 'success' })
      setEmAvaliacao(null)
      await carregar()
    } catch (e) {
      enqueueSnackbar(
        (e as { message?: string })?.message ?? 'Falha ao registrar avaliação',
        { variant: 'error' }
      )
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Candidaturas
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Profissionais que se candidataram a consultor. O cadastro completo só é liberado
        após a aprovação.
      </Typography>

      <FormControl size="small" sx={{ mb: 2, minWidth: 220 }}>
        <InputLabel id="filtro-status-label">Status</InputLabel>
        <Select
          labelId="filtro-status-label"
          id="filtro-status"
          label="Status"
          value={filtro}
          onChange={(e) => setFiltro(e.target.value as StatusCandidatura | 'TODAS')}
        >
          <MenuItem value="TODAS">Todas</MenuItem>
          {(Object.keys(ROTULO_STATUS_CANDIDATURA) as StatusCandidatura[]).map((s) => (
            <MenuItem key={s} value={s}>
              {ROTULO_STATUS_CANDIDATURA[s]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro}
        </Alert>
      )}

      {!carregando && candidaturas.length === 0 ? (
        <Alert severity="info">Nenhuma candidatura neste filtro.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Contato</TableCell>
                <TableCell>Recebida em</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {candidaturas.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{c.nome}</Typography>
                    {c.resumo && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {c.resumo.length > 120 ? `${c.resumo.slice(0, 120)}…` : c.resumo}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{c.email}</Typography>
                    <Typography variant="caption" color="text.secondary">{c.telefone}</Typography>
                  </TableCell>
                  <TableCell>{formatDate(c.createdAt)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={ROTULO_STATUS_CANDIDATURA[c.status]}
                      color={COR_STATUS_CANDIDATURA[c.status]}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {/* Terminal não tem para onde ir — o botão some em vez de abrir
                        um diálogo sem opções. */}
                    {c.proximosStatus.length > 0 && (
                      <Button size="small" onClick={() => abrirAvaliacao(c)}>
                        Avaliar
                      </Button>
                    )}
                    {/* Cadastro submetido: a Originei valida antes de liberar (Etapa 3). */}
                    {['CADASTRO_EM_ANALISE', 'ATIVO', 'PENDENCIA_CADASTRAL', 'BLOQUEADO'].includes(
                      c.status
                    ) && (
                      <Button size="small" onClick={() => setAnalisando(c.id)}>
                        Ver cadastro
                      </Button>
                    )}
                    {/* Aprovada ou já convidada: emitir de novo revoga o link anterior. */}
                    {(c.status === 'APROVADA' || c.status === 'CONVITE_ENVIADO') && (
                      <Button size="small" disabled={emitindo} onClick={() => emitirConvite(c)}>
                        {c.status === 'APROVADA' ? 'Gerar convite' : 'Reenviar convite'}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <AnalisarCadastroDialog
        candidaturaId={analisando}
        onFechar={() => setAnalisando(null)}
        onAprovado={carregar}
      />

      {/* O token aparece UMA ÚNICA VEZ: o banco guarda só o hash e não há como
          recuperá-lo. Fechar sem copiar significa emitir outro convite. */}
      <Dialog open={!!convite} onClose={() => setConvite(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Convite emitido</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {convite && (
            <Stack spacing={2}>
              {convite.emailEnviado ? (
                <Alert severity="success">
                  Convite enviado por e-mail para{' '}
                  <strong>{convite.emailDestinatario}</strong>. O link abaixo é uma cópia —
                  útil se o candidato não encontrar o e-mail. Ele não pode ser recuperado
                  depois de fechar este diálogo.
                </Alert>
              ) : (
                <Alert severity="warning">
                  Não foi possível enviar o e-mail automaticamente
                  {convite.emailDestinatario ? ` para ${convite.emailDestinatario}` : ''} —
                  copie o link agora e envie você mesmo. Ele não pode ser recuperado depois;
                  se perder, será preciso emitir outro convite, e o atual deixa de valer.
                </Alert>
              )}
              <TextField
                label="Link do convite"
                value={linkAbsoluto(convite)}
                fullWidth
                size="small"
                InputProps={{ readOnly: true }}
                onFocus={(e) => e.target.select()}
              />
              <Typography variant="caption" color="text.secondary">
                Expira em {new Date(convite.expiraEm).toLocaleString('pt-BR')}.
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (convite) navigator.clipboard?.writeText(linkAbsoluto(convite))
            }}
          >
            Copiar link
          </Button>
          <Button variant="contained" onClick={() => setConvite(null)}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!emAvaliacao} onClose={() => setEmAvaliacao(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Avaliar candidatura</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {emAvaliacao && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2">{emAvaliacao.nome}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {emAvaliacao.email} · {emAvaliacao.telefone}
                </Typography>
              </Box>
              {emAvaliacao.resumo && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                    {emAvaliacao.resumo}
                  </Typography>
                </Paper>
              )}

              <FormControl fullWidth size="small">
                <InputLabel id="novo-status-label">Novo status</InputLabel>
                <Select
                  labelId="novo-status-label"
                  id="novo-status"
                  label="Novo status"
                  value={novoStatus}
                  onChange={(e) => setNovoStatus(e.target.value as StatusCandidatura)}
                >
                  {/* Só o que a máquina de estados do backend permite a partir daqui. */}
                  {emAvaliacao.proximosStatus.map((s) => (
                    <MenuItem key={s} value={s}>
                      {ROTULO_STATUS_CANDIDATURA[s]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label={parecerObrigatorio ? 'Motivo da reprovação' : 'Parecer'}
                value={parecer}
                onChange={(e) => setParecer(e.target.value)}
                required={parecerObrigatorio}
                error={parecerObrigatorio && !parecer.trim()}
                helperText={
                  parecerObrigatorio
                    ? 'Obrigatório: reprovação sem motivo não pode ser revisada depois.'
                    : 'Registro da apresentação/entrevista.'
                }
                multiline
                minRows={3}
                fullWidth
                size="small"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmAvaliacao(null)}>Cancelar</Button>
          <Button variant="contained" onClick={salvar} disabled={!podeSalvar || salvando}>
            Registrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default CandidaturasPage
